import { Component, computed, DestroyRef, effect, input, OnDestroy } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OutletsService } from 'listings/services/outlets.service';
import { catchError, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import { BrandsService } from 'settings/services/brands.service';
import { SuppliersService } from 'settings/services/suppliers.service';
import { ListingStatusEnum } from 'shared/constants/listing-statuses';
import { OutletTypesEnum } from 'shared/enums/outlet-types.enum';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { EventBusService, PermissionsService, StorageService } from 'shared/services';
import { CategoryListReorderService } from 'shared/services/category-list-reorder.service';
import { ContextAwareSidebarService } from 'shared/services/context-aware-sidebar.service';
import { ListingPartsStatusService } from 'shared/services/listing-parts-status.service';
import { ListingPartPhase } from 'shared/interfaces/listing-parts.interface';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { ModalService } from 'shared/services/modal.service';
import { ToastService } from 'shared/services/toast.service';
import { extractQueryParamsFromUrl } from 'shared/util/extractQueryParamsFromUrl';
import { removeQueryParamsFromUrl } from 'shared/util/removeQueryParamsFromUrl';

import { ListingUserPreferencesModalComponent } from 'listings/components/listing-header-actions/listing-user-preferences/listing-user-preferences-modal.component';
import { SavingOptionsModalComponent } from 'listings/components/saving-options-modal/saving-options-modal.component';
import { ValidationErrorsComponent } from 'listings/components/validation-errors/validation-errors.component';
import { ContentScoringService } from 'listings/services/content-scoring.service';
import { ListingValidationService } from 'listings/services/listing-validation.service';
import { ListingsService } from 'listings/services/listings.service';
import { RoomsFeatureGroupsService } from 'listings/services/rooms/rooms-feature-groups.service';
import { LoaderComponent } from 'shared/components/loader/loader.component';
import { MenuComponent } from 'shared/components/menu/menu.component';
import { PermissionDirective } from 'shared/directives/permission.directive';
import { TooltipDirective } from 'shared/directives/tooltip.directive';
import { ListingComparisonOrchestratorService } from 'shared/services/listing-comparion-orchestrator.service';

@Component({
    selector: 'app-context-aware-sidebar-actions',
    templateUrl: './context-aware-sidebar-actions.component.html',
    styleUrls: ['./context-aware-sidebar-actions.component.scss'],
    imports: [TranslateModule, LoaderComponent, MenuComponent, PermissionDirective, TooltipDirective],
})
export class ContextAwareSidebarActionsComponent implements OnDestroy {
    readonly validationErrorsTooltip = ValidationErrorsComponent;
    listing = input<any>(null);
    forOutlet = input(false);
    forRoom = input(false);
    bulkMode = input(false);
    bulkListingCount = input<number | null>(null);

    protected readonly ListingStatusEnum = ListingStatusEnum;
    protected readonly environment = environment;
    protected readonly featureFlagEnum = FeatureFlagEnum;

    private outletId = this.contextAwareSidebarService.outletId;
    outlet = this.contextAwareSidebarService.outlet;

    private brand = toSignal(
        toObservable(this.listing).pipe(
            filter(Boolean),
            map((listing) => listing.brandId),
            filter(Boolean),
            distinctUntilChanged(),
            switchMap((brandId) =>
                this.brandsService.getBrandById(brandId).pipe(
                    catchError((error) => {
                        console.error('Failed to load brand by id', error);
                        return of(null);
                    }),
                ),
            ),
        ),
        { initialValue: null },
    );

    private brandSideEffects = effect(() => {
        const brand = this.brand();
        if (brand) {
            this.suppliersService.currentSupplierByBrand.set(brand.supplier);
            this.categoryListReorderService.brandDataForResetCall.set(brand);
        }
    });

    protected data = computed(() => {
        const listing = this.listing();
        const forOutlet = this.forOutlet();
        const forRoom = this.forRoom();
        const status = listing?.listingStatus;
        const isReadOnly = listing?.isReadOnly;

        const disableActions = status === ListingStatusEnum.PUBLISHING_IN_PROGRESS && !isReadOnly;
        const contentPhase = this.listingPartsStatusService.contentPhase();
        const showPublishing = contentPhase === ListingPartPhase.LOCKED && !isReadOnly;
        const showSavingOptions = !showPublishing && !isReadOnly;
        const allPartsLive = this.listingPartsStatusService.allPartsLive();
        const isProcessing = [ListingStatusEnum.BULK_PROCESSING, ListingStatusEnum.PUBLISHING_IN_PROGRESS].includes(status);
        const isLive = status === ListingStatusEnum.LIVE;

        const listingIsDeletable = !isProcessing && !isReadOnly;
        const showDelete = forOutlet || forRoom ? listingIsDeletable && !isLive : listingIsDeletable;

        const featureFlags = this.featureFlagService.featureFlags();
        const showUserPreferences =
            featureFlags[FeatureFlagEnum.RESTRICTIVE_UI] || featureFlags[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON];

        const isUnifiedListingMode = !!featureFlags[FeatureFlagEnum.UNIFIED_LISTING];
        const isComparisonActive = this.listingComparisonOrchestrator.isComparisonActive();
        const canOpenVersionComparison = this.listingComparisonOrchestrator.canOpenVersionComparison();

        const showStartVersionComparison = isUnifiedListingMode && canOpenVersionComparison && !isComparisonActive;
        const showExitComparison = isUnifiedListingMode && isComparisonActive;

        const showMenu = showDelete || showUserPreferences || showStartVersionComparison || showExitComparison;

        return {
            disableActions,
            showPublishing,
            showSavingOptions,
            allPartsLive,
            showDelete,
            showUserPreferences,
            showStartVersionComparison,
            showExitComparison,
            showMenu,
        };
    });

    constructor(
        public featureFlagService: FeatureFlagService,
        public contentScoringService: ContentScoringService,
        public listingValidationService: ListingValidationService,
        private modalService: ModalService,
        private listingsService: ListingsService,
        private outletService: OutletsService,
        private translate: TranslateService,
        private router: Router,
        private destroyRef: DestroyRef,
        private toastService: ToastService,
        private storageService: StorageService,
        private contextAwareSidebarService: ContextAwareSidebarService,
        private listingPartsStatusService: ListingPartsStatusService,
        private brandsService: BrandsService,
        private eventBusService: EventBusService,
        private suppliersService: SuppliersService,
        protected permissionsService: PermissionsService,
        private categoryListReorderService: CategoryListReorderService,
        private listingComparisonOrchestrator: ListingComparisonOrchestratorService,
        private roomsFeatureGroupsService: RoomsFeatureGroupsService,
    ) {}

    ngOnDestroy(): void {
        this.categoryListReorderService.brandDataForResetCall.set(null);
    }

    protected triggerBulkSaveAndActivate(): void {
        this.eventBusService.dispatch('listing:bulkEdit:saveAndActivate', undefined);
    }

    protected triggerBulkOpenListingList(): void {
        this.eventBusService.dispatch('listing:bulkEdit:openListingList', undefined);
    }

    protected openVersionComparisonModal(): void {
        this.listingComparisonOrchestrator.openVersionComparisonModal();
    }

    protected exitComparison(): void {
        this.listingComparisonOrchestrator.endComparison();
    }

    protected deleteListing(): void {
        const listing = this.listing();
        this.modalService
            .openDeleteConfirmationModal(
                this.translate.instant('listing.deleteModal.body'),
                this.translate.instant('listing.deleteModal.title'),
                undefined,
                listing.name,
                this.translate.instant('listing.deleteModal.entityTypeName'),
                true,
                listing.name,
                undefined,
                true,
            )
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => this.listingsService.deleteListing(listing.id)),
            )
            .subscribe(() => {
                this.toastService.showToast('listing.deleteSuccess', 'success', {
                    listing: `${listing.shijiId} - ${listing.name}`,
                });
                const returnUrl = this.storageService.getFromSession('listingsReturnPage') as string;
                if (returnUrl) {
                    const baseUrl = removeQueryParamsFromUrl(returnUrl);
                    const queryParams = extractQueryParamsFromUrl(returnUrl);
                    this.router.navigate([baseUrl], { queryParams });
                } else {
                    this.router.navigate(['/listings']);
                }
            });
    }

    protected deleteOutlet(): void {
        const listing = this.listing();
        const outlet = this.outlet();
        if (!outlet) {
            return;
        }

        this.modalService
            .openDeleteConfirmationModal(
                this.translate.instant('listing.outlet.deleteModal.body'),
                this.translate.instant('listing.outlet.deleteModal.title'),
                undefined,
                outlet.name,
                this.translate.instant('listing.outlet.deleteModal.entityTypeName'),
                true,
                outlet.name,
                undefined,
                true,
            )
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => this.outletService.deleteOutlet(listing.id, outlet.id)),
            )
            .subscribe(() => {
                this.toastService.showToast('listing.outlet.deleteModal.successMessage', 'success', { name: outlet.name });
                this.router.navigate(['/listings', 'details', listing.id]);
            });
    }

    protected deleteRoom(): void {
        const listing = this.listing();
        const roomId = this.contextAwareSidebarService.selectedRoomId();
        const roomName = this.contextAwareSidebarService.roomName()?.roomName ?? '';
        if (!roomId) {
            return;
        }

        this.modalService
            .openDeleteConfirmationModal(
                this.translate.instant('listing.room.deleteModal.body'),
                this.translate.instant('listing.room.deleteModal.title'),
                undefined,
                roomName,
                this.translate.instant('listing.room.deleteModal.entityTypeName'),
                true,
                roomName,
                undefined,
                true,
            )
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => this.roomsFeatureGroupsService.deleteRoom(listing.id, roomId)),
            )
            .subscribe(() => {
                this.toastService.showToast('listing.room.deleteModal.successMessage', 'success', { name: roomName });
                this.router.navigate(['/listings', 'details', listing.id, 'roomType']);
            });
    }

    protected openSavingOptionsModal(): void {
        this.modalService.openModal(
            SavingOptionsModalComponent,
            { listing: this.listing(), type: 'center' },
            { closeOnEscape: false, closeOnOverlayClick: false },
        );
    }

    protected openUserPreferencesDrawer(): void {
        const listing = this.listing();
        const brand = this.brand();
        const outlet = this.outlet();

        if (!brand) {
            return;
        }

        const modalReference = this.modalService.openModal(ListingUserPreferencesModalComponent, {
            type: 'side',
            listingId: listing.id,
            outletId: this.forOutlet() ? this.outletId() : undefined,
            outletTypeId: this.forOutlet() ? OutletTypesEnum[outlet?.outletTypeId as keyof typeof OutletTypesEnum] : OutletTypesEnum.HOTEL,
            brandRestrictiveUiChannels: brand.brandViewSettings.brandViewSettingsChannelRestriction || [],
            listingRestrictiveUiType: brand.brandViewSettings.type,
        });

        modalReference.onClose.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean)).subscribe(() => {
            this.eventBusService.dispatch('listing:reload', { listingId: listing.id });
        });
    }
}
